<template>
  <div class="pane outsideMenuSidebar">
    <div class="outsideMenu">
      <section v-if="has.conference" class="generalMenu">
        <h2>Meetings</h2>
        <ul>
          <li>
            <router-link to="/welcome">Home</router-link>
          </li>
          <li>
            <a href="/conference/schedule/meeting-name" @click="inProgressRequests.scheduledMeetings = true">Schedule</a>
            <app-loading-spinner v-if="inProgressRequests.scheduledMeetings" class="logout-loader" />
          </li>
          <li class="joinItem">
            <router-link to="/join">Join</router-link>
          </li>
          <li>
            <a href="/conference/schedule/meetings">Meeting List</a>
          </li>
          <li>
            <router-link to="/my-room">Personal Room</router-link>
          </li>
          <li v-if="accountStore.canCreate">
            <router-link to="/tutorials">Tutorials</router-link>
          </li>
          <li v-if="!isOpenedWithinElectronShell">
            <a :href="downloadLink" @click="navigate('/app/downloading')">Download App</a>
          </li>
          <li>
            <router-link to="/check">Media Setup</router-link>
          </li>
          <li v-if="accountStore.canCreate">
            <router-link :disabled="isAdvancedSettingsDisabled" to="/advanced-settings">Advanced Settings</router-link>
          </li>
        </ul>
      </section>

      <section v-if="['appIndex', 'joinWithLink'].includes(pageStore.current.pageName)" class="generalMenu">
        <h2>Menu</h2>
        <ul>
          <li>
            <button @click="navigate('/join-with-link')">Join Meeting</button>
          </li>
          <li>
            <a v-if="isOpenedWithinElectronShell" href="#" @click="redirectToAuth('sign_in')">Log In</a>
            <a v-else :href="`/lauth/nowhere?returnUrl=${accountStore.baseUrl}`">Log In</a>
          </li>
        </ul>
      </section>
      <section v-else-if="!has.conference" class="generalMenu">
        <h2 v-if="accountStore.isGuest">Meetings</h2>
        <h2 v-else>Menu</h2>
        <ul>
          <li>
            <router-link to="/join">Join Meeting</router-link>
          </li>
          <li v-if="!isOpenedWithinElectronShell">
            <a :href="downloadLink" @click="navigate('/app/downloading')">Download App</a>
          </li>

          <li v-if="accountStore.isGuest || !accountStore.isLoggedIn">
            <button @click="redirectToAuth('sign_in')">Log In</button>
          </li>
        </ul>
      </section>

      <section v-if="has.account && has.type === 'owner' && (has.account.bixe_ea_account === 2 || has.account.bixe_ea_account === 4)" class="accountMenu">
        <h2>{{ has.account.name }}</h2>
        <ul>
          <li>
            <a href="#" @click="getAutoLoginLinkAndRedirect($event, 'accountSettingsPage')">Account Settings</a>
            <app-loading-spinner v-if="inProgressRequests.accountSettingsPage" class="inline-loader" />
          </li>
          <li />
        </ul>
      </section>
      <section class="userMenu" v-if="userName">
        <h2>{{ userName }}</h2>
        <ul>
          <li v-if="!accountStore.isGuest">
            <a href="#" @click="getAutoLoginLinkAndRedirect($event, 'userSettingsPage')">User Settings</a>
            <app-loading-spinner v-if="inProgressRequests.userSettingsPage" class="inline-loader" />
          </li>
          <li v-if="!accountStore.isGuest && accountSettingsUrl && has.account.bixe_ea_account === 1">
            <a href="#" @click="getAutoLoginLinkAndRedirect($event, 'accountSettingsPage')">Account Settings</a>
            <app-loading-spinner v-if="inProgressRequests.accountSettingsPage" class="inline-loader" />
          </li>
          <li>
            <a v-if="isOpenedWithinElectronShell" href="#" @click="openExternal('https://www.bixe.ai/support.html')">Support Request</a>
            <a v-else href="https://www.bixe.ai/support.html" target="_blank" rel="noopener">Support Request</a>
          </li>
          <li v-if="!accountStore.isGuest">
            <a :href="logoutUrl">Log Out</a>
          </li>
        </ul>
      </section>
      <section class="switcher" v-if="!accountStore.isGuest">
        <ul>
          <li>
            <button v-if="isOpenedWithinElectronShell" @click="openExternal(accountSwitcherUrl)">Switch Account</button>
            <a v-else target="_blank" :href="accountSwitcherUrl">Switch Account</a>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '@/store/pinia/account';
import { usePageStore } from '@/store/pinia/page';
import { useSidebarStore } from '@/store/pinia/sidebar';
import utils from '../resources/utils';
import AppLoadingSpinner from '@/components/AppLoadingSpinner';
import errorReportingService from '@/services/errorReportingService';
const accountStore = useAccountStore();
const pageStore = usePageStore();
const sidebarStore = useSidebarStore();

const { $API } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const inProgressRequests = ref({ userSettingsPage: false, accountSettingsPage: false, scheduledMeetings: false });
const isOpenedWithinElectronShell = ref(utils.isOpenedWithinElectronShell());
const downloadLink = ref(utils.genericFns.getDownloadAppLink());

const { permissions: has, accountUrl, authUrl, user, isGuest } = storeToRefs(accountStore);
const { current: currentPage } = storeToRefs(pageStore);
const userName = computed(() => user.value?.name);
const accountSettingsUrl = computed(() => has.value.account && has.value.account.account_setting_url);
const isAdvancedSettingsDisabled = computed(() => currentPage.value.pageName === 'connectivity');
const accountSwitcherUrl = computed(() => {
  const returnPath = isOpenedWithinElectronShell.value ? '/conference/app/post-login' : '/conference/welcome';
  const client = isOpenedWithinElectronShell.value ? 'bixe-app' : 'bixe-web';
  const { origin } = new URL(accountStore.centralAuthUrl); // parsing to fix trailing slash issue
  return `${origin}/switcher?redirect_path=${returnPath}&client=${client}`;
});
const logoutUrl = computed(() => {
  const bixeUrl = window.location;
  if (isOpenedWithinElectronShell.value) {
    return `${window.location.origin}/logout?returnUrl=${bixeUrl.origin}/conference/app/welcome`;
  }
  const tenantAuthUrl = authUrl.value.replace('apps', accountStore.tenantName).replace('tenantname', accountStore.tenantName);
  const coreLogoutUrl = `${tenantAuthUrl}/auth/logout?skipBixeLogout=true`;
  return `${window.location.origin}/logout?returnUrl=${coreLogoutUrl}`;
});

function navigate(url) {
  router.push(url).catch(() => null);
}
function openExternal(url, closeSelf = false) {
  ipcApi.send({ name: 'openExternal', url: url, closeSelf });
}

onUnmounted(() => {
  if (isOpenedWithinElectronShell.value) {
    setTimeout(() => {
      document.querySelector('#mainSidebar').style.display = 'none';
    }, 500);
  }
});

onMounted(() => {
  if (isOpenedWithinElectronShell.value) {
    // ugly hack to force repaint on electron shell to avoid blank screen until window resized
    document.querySelector('#mainSidebar').style.display = 'block';
  }
});

async function redirectToAuth(action) {
  if (isGuest.value) {
    await $API.attemptLogout(true);
  }
  if (isOpenedWithinElectronShell.value) {
    openExternal(`${window.location.origin}/lauth/nowhere?returnUrl=/conference/app/post-login&action=${action}`, false);
    return;
  }
  window.location = `/lauth/nowhere?returnUrl=/conference&action=${action}`;
}

function getAutoLoginLinkAndRedirect(eve, destination) {
  eve.preventDefault();
  if (inProgressRequests.value[destination]) {
    return; // Prevent multiple clicks on the same link
  }
  inProgressRequests.value[destination] = true;
  let redirectLink = '';
  if (destination === 'userSettingsPage') {
    redirectLink = accountUrl.value;
  } else if (destination === 'accountSettingsPage') {
    redirectLink = has.value.account?.account_setting_url;
  }

  $API
    .getAutoLoginMagicLink({ redirectLink })
    .then((response) => {
      let destinationUrl = response?.link;
      if (!destinationUrl) {
        const tenantAuthUrl = authUrl.value.replace('apps', accountStore.tenantName).replace('tenantname', accountStore.tenantName);
        destinationUrl = redirectLink.startsWith(tenantAuthUrl) ? redirectLink : `${tenantAuthUrl}${redirectLink}`;
      }
      if (isOpenedWithinElectronShell.value) {
        openExternal(destinationUrl, false);
      } else {
        window.open(destinationUrl, '_blank');
      }
    })
    .catch((err) => {
      errorReportingService.reportError(err);
    })
    .finally(() => {
      if (['userSettingsPage', 'accountSettingsPage'].includes(destination)) {
        sidebarStore.requestPane(false, {}, 'hamburgerButton');
      }
      inProgressRequests.value[destination] = false;
    });
}

window.addEventListener('pageshow', () => {
  inProgressRequests.value = { userSettingsPage: false, accountSettingsPage: false, logout: false, scheduledMeetings: false };
});
</script>
<style lang="scss" scoped>
.outsideMenu {
  padding: rem(125px) 0 0 rem(57px);
  section + section {
    margin-top: rem(31px);
  }

  ul {
    list-style: none;
    line-height: math.div(22, 18);
  }

  h2,
  button,
  a {
    @include type-size('medium');
    line-height: math.div(22, 18);
  }

  h2 {
    font-weight: bold;
  }

  li {
    margin-top: rem(4.5px);
    .inline-loader,
    .logout-loader {
      display: inline-block;
      margin-left: 6px;
    }
  }

  button,
  a,
  a:visited {
    padding: 0;
    font-weight: 200;
    color: var(--c__text);
    @include triggered {
      color: var(--c__accent);
    }
  }

  button,
  a,
  a:visited {
    &[disabled='true'] {
      color: var(--c__disabled);
      pointer-events: none;
      @include triggered {
        color: var(--c__disabled);
      }
    }
  }
}
.joinItem {
  margin-bottom: 2em;
}
</style>
